import axiosInstance from './api'
import TokenService from './token.service'
import EventBus from '../common/EventBus'
import api from './api'

const setupInterceptors = (store) => {
  let refreshInProgress = false
  let pendingRequests = []

  // Attach access token to every request
  axiosInstance.interceptors.request.use(
    (config) => {
      const accessToken = TokenService.getLocalAccessToken()
      if (accessToken) {
        config.headers['Authorization'] = 'Bearer ' + accessToken
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  // Helper to retry all queued requests after refresh token
  const retryPendingRequests = (error, newAccessToken = null) => {
    pendingRequests.forEach((queuedRequest) => {
      if (error) {
        queuedRequest.reject(error)
      } else {
        queuedRequest.resolve(newAccessToken)
      }
    })
    pendingRequests = []
  }

  // Handle 401 and refresh token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If network error, reject immediately
      if (!error.response) {
        return Promise.reject(error)
      }

      // Don't refresh token for auth endpoints
      const isAuthEndpoint =
        originalRequest.url.includes('admin/auth/login') ||
        originalRequest.url.includes('/verify-email') ||
        originalRequest.url.includes('/refresh-token')

      if (error.response.status === 401 && !isAuthEndpoint) {
        // If request was already retried once, logout
        if (originalRequest.hasRetried) {
          EventBus.dispatch('logout')
          return Promise.reject(error)
        }

        // If refresh is already in progress, queue this request
        if (refreshInProgress) {
          return new Promise((resolve, reject) => {
            pendingRequests.push({
              resolve: (newAccessToken) => {
                // Retry the request with new token
                originalRequest.headers['Authorization'] =
                  'Bearer ' + newAccessToken
                resolve(axiosInstance(originalRequest))
              },
              reject,
            })
          })
        }

        // Start refresh token process
        originalRequest.hasRetried = true
        refreshInProgress = true

        try {
          const refreshToken = TokenService.getLocalRefreshToken()
          if (!refreshToken) {
            EventBus.dispatch('logout')
            return Promise.reject(error)
          }

          const response = await api.post('/api/auth/refresh-token', {
            refresh_token: refreshToken,
          })

          const newAccessToken = response.data.access_token
          store.dispatch('auth/refreshToken', newAccessToken)
          TokenService.updateLocalAccessToken(newAccessToken)

          refreshInProgress = false
          retryPendingRequests(null, newAccessToken)

          // Retry the original request with new token
          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          refreshInProgress = false
          retryPendingRequests(refreshError, null)
          TokenService.removeUser()
          EventBus.dispatch('logout')
          return Promise.reject(refreshError)
        } finally {
          refreshInProgress = false
        }
      }

      // For other errors, just reject
      return Promise.reject(error)
    },
  )
}

export default setupInterceptors
